import { env } from '@/lib/env'

import type React from 'react'
import type { LocaleText, Translations } from '@/gen/sanity/schema'

export type TranslationLanguage = keyof Omit<LocaleText, '_type'>
export type TranslationEntry = Pick<Translations, 'key' | 'text'>
type TranslationValues = Record<string, React.ReactNode>

interface TranslateTextOptions {
  translations: TranslationEntry[] | null | undefined
  language: TranslationLanguage
  key: string
  values?: TranslationValues
}

// FIXME: This const is on server also - not ideal
const loggedMissingTranslations = new Set<string>()

export interface MissingTranslationLog {
  key: string
  language: TranslationLanguage
  message: string
}

type MissingTranslationLogger = (log: MissingTranslationLog) => void

const serverTranslationLoggerStore = globalThis as typeof globalThis & {
  __serverMissingTranslationLogger?: MissingTranslationLogger
}

export function registerServerTranslationLogger(logger: MissingTranslationLogger): void {
  serverTranslationLoggerStore.__serverMissingTranslationLogger = logger
}

const logMissingTranslation = ({ key, language }: { key: string; language: TranslationLanguage }) => {
  const logKey = `${language}:${key}`
  if (loggedMissingTranslations.has(logKey)) {
    return
  }
  loggedMissingTranslations.add(logKey)

  const message = `[TranslationError] Missing translation key: ${key} for language: ${language}`

  if (typeof window === 'undefined') {
    const serverLogger = serverTranslationLoggerStore.__serverMissingTranslationLogger
    if (serverLogger) {
      serverLogger({ key, language, message })
      return
    }

    console.error(message)
    return
  }

  if (typeof console === 'undefined') {
    return
  }

  // Client-side: DataDog browser SDK forwards console.warn via forwardConsoleLogs.
  const group = console.groupCollapsed ?? console.group
  const groupEnd = console.groupEnd

  try {
    group?.call(console, message)
    console.warn?.(new Error())
  } finally {
    groupEnd?.call(console)
  }
}

export const translateText = ({ translations, language, key, values }: TranslateTextOptions): React.ReactNode => {
  const translation = translations?.find((value) => value.key === key)
  const text = translation?.text?.[language]

  if (translation == null) {
    return formatMissingTranslation({ key, language, values, wrap: 'double' })
  }

  if (text == null) {
    return formatMissingTranslation({ key, language, values, wrap: 'single' })
  }

  if (values == null) {
    return text
  }

  return interpolateValues(text, values)
}

export const formatMissingTranslation = ({
  key,
  language,
  values,
  wrap,
}: {
  key: string
  language: TranslationLanguage
  values?: TranslationValues
  wrap: 'single' | 'double'
}): string => {
  logMissingTranslation({ key, language })

  if (env.AZ_ENV === 'p') {
    return ''
  }

  const valuesSuffix = values != null ? ' ' + JSON.stringify(values).replaceAll('"', '') : ''
  const marker = `[${language.toUpperCase()}]${key}${valuesSuffix}`

  return wrap === 'double' ? `$$${marker}$$` : `$${marker}$`
}

export const interpolateValues = (text: string, values: TranslationValues): React.ReactNode => {
  const parts = text.split(/\{\{([^{}]*)\}\}/g)
  const result: React.ReactNode[] = []
  let hasElement = false

  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      if (parts[i]) {
        result.push(parts[i])
      }
      continue
    }

    const placeholder = parts[i]
    const value = values[placeholder]
    if (value == null) {
      result.push(`{{${placeholder}}}`)
      continue
    }

    if (typeof value === 'string' || typeof value === 'number') {
      result.push(value.toString())
      continue
    }

    hasElement = true
    result.push(value)
  }

  if (!hasElement) {
    return result.join('')
  }

  if (result.length === 1 && typeof result[0] === 'string') {
    return result[0]
  }

  return result
}

export const pickTranslationLanguage = (
  translations: TranslationEntry[],
  language: TranslationLanguage
): TranslationEntry[] =>
  translations.map(({ key, text }) => ({ key, text: text && { _type: text._type, [language]: text[language] } }))

export function createTranslator(options: Omit<TranslateTextOptions, 'key' | 'values'>) {
  return (key: string, values?: TranslationValues) => translateText({ ...options, key, values })
}
